import { c as _c } from "react-compiler-runtime";
import { useEffect } from 'react';
import { useCellSelectionDispatch, useCellSelectionSelector } from '../../redux/reduxHooks';
import { useCellSelectionScopeId } from '../context/CellSelectionContext';
import { dragOver, endDrag, getIsDragging } from '../redux/cellSelectionSlice';
import { CELL_SELECTABLE_ATTRIBUTE } from '../../table/constants/cellSelectionAttributes';

// Cells carry their coordinates as DOM attributes (see Cell.tsx). We read them
// off whatever cell is under the pointer to translate a scroll position back
// into a { rowIndex, columnIndex } for the reducer.
const ROW_INDEX_ATTRIBUTE = 'data-fdt-row-index';
const COLUMN_INDEX_ATTRIBUTE = 'data-column-index';

// Distance (px) from a scroll-container edge at which auto-scroll engages while
// a cell-selection drag is in progress.
const EDGE_THRESHOLD = 60;
// Peak scroll distance (px) applied per animation frame, reached at the edge.
const MAX_SCROLL_SPEED = 24;
// How many times to step the cell hit-test inward from the edge before giving
// up, so a query that lands on a scrollbar/overhang/blank strip still resolves
// a real cell.
const HIT_TEST_INWARD_STEPS = 4;

// Eases the scroll speed from 0.1x (at the threshold boundary) up to 1x (at the
// edge) so scrolling accelerates as the pointer nears the edge. Never returns 0
// — that would stall the requestAnimationFrame loop while still in the zone.
const calculateScrollEasing = distanceFromEdge => {
  const easeFactor = Math.max(0, Math.min(1, (EDGE_THRESHOLD - distanceFromEdge) / EDGE_THRESHOLD));
  return 0.1 + 0.9 * easeFactor;
};
/**
 * Auto-scrolls the table while a cell-selection drag (plain click-drag,
 * modifier-drag, or fill-handle drag) is in progress and the pointer is held
 * near an edge of the scroll container, so a selection can be extended past the
 * currently visible rows/columns.
 *
 * Only active while a drag is in progress (`isDragging`), so there are no
 * document listeners otherwise. Scroll speed eases with edge proximity, and
 * both axes are handled every frame: the top/bottom edges drive vertical
 * scroll (`scrollTop`) and the left/right edges drive horizontal scroll
 * (`scrollLeft`), so a fill drag can extend down, right, or diagonally.
 *
 * The reducer extends the active range on `mouseenter` per cell, but that event
 * does not fire for content scrolling beneath a stationary pointer. So each
 * frame we resolve the cell under the (edge-clamped) pointer via
 * `elementFromPoint` and dispatch `dragOver`, marching the selection along as
 * new cells stream in from virtualization.
 */
export const useAutoScrollWhileCellDrag = t0 => {
  const $ = _c(6);
  const {
    scrollContainerRef
  } = t0;
  const dispatch = useCellSelectionDispatch();
  const isDragging = useCellSelectionSelector(getIsDragging);
  const scopeId = useCellSelectionScopeId();
  let t1;
  let t2;
  if ($[0] !== dispatch || $[1] !== isDragging || $[2] !== scopeId || $[3] !== scrollContainerRef) {
    t1 = () => {
      if (!isDragging) {
        return;
      }
      let frameId = null;
      let pointer = null;
      const resolveCellAt = (x, y) => {
        var _document$elementFrom;
        const cell = (_document$elementFrom = document.elementFromPoint(x, y)) === null || _document$elementFrom === void 0 ? void 0 : _document$elementFrom.closest(`[${ROW_INDEX_ATTRIBUTE}]`);
        if (!cell || cell.getAttribute(CELL_SELECTABLE_ATTRIBUTE) !== scopeId) {
          return null;
        }
        return cell;
      };
      const extendToPoint = (x_0, y_0, centerX, centerY) => {
        let cell_0 = resolveCellAt(x_0, y_0);
        for (let stepIndex = 1; !cell_0 && stepIndex <= HIT_TEST_INWARD_STEPS; stepIndex++) {
          const t = stepIndex / (HIT_TEST_INWARD_STEPS + 1);
          cell_0 = resolveCellAt(x_0 + (centerX - x_0) * t, y_0 + (centerY - y_0) * t);
        }
        if (!cell_0) {
          return;
        }
        const rowIndex = Number(cell_0.getAttribute(ROW_INDEX_ATTRIBUTE));
        const columnIndex = Number(cell_0.getAttribute(COLUMN_INDEX_ATTRIBUTE));
        if (Number.isNaN(rowIndex) || Number.isNaN(columnIndex)) {
          return;
        }
        dispatch(dragOver({
          rowIndex,
          columnIndex
        }));
      };
      function step() {
        const container = scrollContainerRef.current;
        if (!container || !pointer) {
          frameId = null;
          return;
        }
        const rect = container.getBoundingClientRect();
        const viewTop = Math.max(rect.top, 0);
        const viewBottom = Math.min(rect.bottom, window.innerHeight);
        const viewLeft = Math.max(rect.left, 0);
        const viewRight = Math.min(rect.right, window.innerWidth);
        const distTop = pointer.y - viewTop;
        const distBottom = viewBottom - pointer.y;
        const distLeft = pointer.x - viewLeft;
        const distRight = viewRight - pointer.x;
        let dy = 0;
        if (distTop <= distBottom) {
          if (distTop < EDGE_THRESHOLD) {
            dy = -MAX_SCROLL_SPEED * calculateScrollEasing(distTop);
          }
        } else {
          if (distBottom < EDGE_THRESHOLD) {
            dy = MAX_SCROLL_SPEED * calculateScrollEasing(distBottom);
          }
        }
        let dx = 0;
        if (distLeft <= distRight) {
          if (distLeft < EDGE_THRESHOLD) {
            dx = -MAX_SCROLL_SPEED * calculateScrollEasing(distLeft);
          }
        } else {
          if (distRight < EDGE_THRESHOLD) {
            dx = MAX_SCROLL_SPEED * calculateScrollEasing(distRight);
          }
        }
        if (dx === 0 && dy === 0) {
          frameId = null;
          return;
        }
        const prevTop = container.scrollTop;
        const prevLeft = container.scrollLeft;
        container.scrollTop = prevTop + dy;
        container.scrollLeft = prevLeft + dx;
        const didScroll = container.scrollTop !== prevTop || container.scrollLeft !== prevLeft;
        const clampedX = Math.max(viewLeft + 1, Math.min(viewRight - 1, pointer.x));
        const clampedY = Math.max(viewTop + 1, Math.min(viewBottom - 1, pointer.y));
        extendToPoint(clampedX, clampedY, (viewLeft + viewRight) / 2, (viewTop + viewBottom) / 2);
        if (!didScroll) {
          frameId = null;
          return;
        }
        frameId = requestAnimationFrame(step);
      }
      const handleMouseMove = function handleMouseMove(e) {
        if ((e.buttons & 1) === 0) {
          terminateDrag();
          return;
        }
        pointer = {
          x: e.clientX,
          y: e.clientY
        };
        if (frameId === null) {
          frameId = requestAnimationFrame(step);
        }
      };
      const stopAutoScroll = function stopAutoScroll() {
        document.removeEventListener("mousemove", handleMouseMove);
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
          frameId = null;
        }
      };
      function terminateDrag() {
        stopAutoScroll();
        dispatch(endDrag());
      }
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopAutoScroll);
      document.addEventListener("mousedown", stopAutoScroll, true);
      window.addEventListener("blur", terminateDrag);
      return () => {
        document.removeEventListener("mouseup", stopAutoScroll);
        document.removeEventListener("mousedown", stopAutoScroll, true);
        window.removeEventListener("blur", terminateDrag);
        stopAutoScroll();
      };
    };
    t2 = [isDragging, scrollContainerRef, dispatch, scopeId];
    $[0] = dispatch;
    $[1] = isDragging;
    $[2] = scopeId;
    $[3] = scrollContainerRef;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
};